import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  FELJEGYZÉS-TÍPUSOK (dicséret, intő, felszereléshiány…) – minden paraméterükkel.
 *
 *  A feljegyzés a típus NEVÉT tárolja, ezért az átnevezést a kiszolgáló a meglévő
 *  feljegyzéseken is átvezeti (és az ikon változását is) – itt ezt csak megmondjuk.
 *  Használt típus nem törölhető, csak inaktiválható. A „Csak admin" oszlop dönti el, melyiket
 *  rögzítheti a pedagógus (fokozatok, tanórai típusok) és melyiket csak admin (határozat, záradék,
 *  törzslapra kerülő bejegyzés).
 */
@Component({
  selector: 'app-admin-feljegyzes-tipus',
  templateUrl: './feljegyzes-tipus.component.html',
  styleUrls: ['./feljegyzes-tipus.component.scss'],
})
export class AdminFeljegyzesTipusComponent implements OnInit {
  sajatIntezmeny: any;
  tipusok: any[] = [];
  toltodik = false;
  hiba = '';
  ujNyitva = false;
  uj: any = this.uresUj();

  constructor(private http: HttpClient, private data: DataService) {}

  private uresUj() {
    return { nev: '', kod: '', icon: 'document-text-outline', leiras: '', kreta_kod: '', kreta_id: '', sorrend: 999, aktiv: true,
      csak_admin: false, kreta_visszairas: true };
  }

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    await this.betolt();
  }

  private get intezmenyQs(): string {
    return 'intezmeny=' + encodeURIComponent(String(this.sajatIntezmeny?.intezmenyId || '').replace(/^\/+|\/+$/g, ''));
  }

  async betolt() {
    this.toltodik = true;
    this.hiba = '';
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/feljegyzes-tipusok?${this.intezmenyQs}`));
      this.tipusok = (Array.isArray(r) ? r : []).map((t: any) => ({ ...t, eredeti: { ...t } }));
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A típusok nem tölthetők be.';
    } finally {
      this.toltodik = false;
    }
  }

  ikonElonezet(ikon: string): string {
    return /^[a-z0-9-]+$/.test(String(ikon || '')) ? ikon : 'help-circle-outline';
  }

  /*  `csak_admin`: a határozatot, záradékot, törzslapra kerülő bejegyzést csak admin rögzítheti –
   *  a pedagógus választójában nem jelenik meg, és a kiszolgáló a mentésnél is elutasítja. */
  //  `kreta_visszairas`: van-e KRÉTA-megfelelője; ha nincs (pl. a korlátozott tárgy engedélye), a
  //  feljegyzés csak a SULINFORM-ban él, a visszaíró kihagyja.
  private static readonly MEZOK = ['nev', 'kod', 'icon', 'leiras', 'kreta_kod', 'kreta_id', 'sorrend', 'aktiv', 'csak_admin',
    'kreta_visszairas'];

  /** A típus saját mezőinek felsorolása (a sémát a kiszolgáló adja, itt csak olvasható). */
  mezoLista(t: any): string {
    return (Array.isArray(t?.mezok) ? t.mezok : []).map((m: any) => m?.cimke).filter(Boolean).join(', ');
  }

  modosult(t: any): boolean {
    return AdminFeljegyzesTipusComponent.MEZOK.some((m) =>
      String(t[m] ?? '') !== String(t.eredeti?.[m] ?? ''));
  }

  private torzs(t: any) {
    const k: any = {};
    for (const m of AdminFeljegyzesTipusComponent.MEZOK) k[m] = t[m];
    return k;
  }

  async ment(t: any) {
    const atnevezes = t.eredeti && t.eredeti.nev !== t.nev && t.hasznalat > 0;
    if (atnevezes && !confirm(`A(z) „${t.eredeti.nev}" típust ${t.hasznalat} feljegyzés használja. `
      + `Átnevezéskor ezeken is „${t.nev}" lesz a típus neve. Folytatod?`)) return;
    try {
      const r: any = await firstValueFrom(this.http.put(`/api/feljegyzes-tipusok/${t.id}?${this.intezmenyQs}`, this.torzs(t)));
      const reszlet = [
        r?.atnevezett_feljegyzes ? `${r.atnevezett_feljegyzes} feljegyzés átnevezve` : '',
        r?.ikon_frissitve ? `${r.ikon_frissitve} feljegyzés ikonja frissítve` : '',
      ].filter(Boolean).join(', ');
      this.data.sendToast('Mentve.' + (reszlet ? ` (${reszlet})` : ''), 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    }
  }

  async aktivValt(t: any) {
    const kert = { ...this.torzs(t.eredeti), aktiv: !t.eredeti.aktiv };
    try {
      await firstValueFrom(this.http.put(`/api/feljegyzes-tipusok/${t.id}?${this.intezmenyQs}`, kert));
      this.data.sendToast(kert.aktiv
        ? `„${t.eredeti.nev}" újra választható.`
        : `„${t.eredeti.nev}" inaktív – új feljegyzéshez nem választható, a meglévők maradnak.`, 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A módosítás nem sikerült.', 'error');
    }
  }

  torlesCim(t: any): string {
    return t.hasznalat ? `${t.hasznalat} feljegyzés használja – nem törölhető, de inaktiválható` : 'Törlés';
  }

  async torol(t: any) {
    if (!confirm(`Törlöd a(z) „${t.nev}" típust?`)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/feljegyzes-tipusok/${t.id}?${this.intezmenyQs}`));
      this.data.sendToast('Törölve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }

  async ujMent() {
    if (!String(this.uj.nev || '').trim()) { this.data.sendToast('Adj nevet a típusnak.', 'warning'); return; }
    try {
      await firstValueFrom(this.http.post(`/api/feljegyzes-tipusok?${this.intezmenyQs}`, this.uj));
      this.data.sendToast('Típus felvéve – mostantól választható a feljegyzéseknél.', 'success');
      this.uj = this.uresUj();
      this.ujNyitva = false;
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A felvétel nem sikerült.', 'error');
    }
  }
}
